'use client'

import * as Sentry from '@sentry/browser'
import { useEffect } from 'react'

const isDev = process.env.NODE_ENV === 'development'

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    // Capture comprehensive error context
    const errorContext = {
      extra: {
        digest: error.digest,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : 'SSR',
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'SSR'
      },
      tags: {
        errorType: 'global-error',
        page: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      }
    }

    // Log to Sentry
    Sentry.captureException(error, errorContext)

    // Always log to console for debugging
    console.error('🚨 Global Error Caught:', {
      message: error.message,
      name: error.name,
      digest: error.digest,
      stack: error.stack,
      url: typeof window !== 'undefined' ? window.location.href : 'SSR'
    })
  }, [error])

  return (
    <html>
      <body>
        <div
          style={{
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              maxWidth: '600px',
              padding: '40px',
              textAlign: 'center',
              width: '100%'
            }}
          >
            <h1 style={{ color: '#dc3545', fontSize: '24px', marginBottom: '16px' }}>Application Error</h1>
            <p style={{ color: '#6c757d', marginBottom: '24px' }}>
              A client-side exception has occurred. Our team has been notified.
            </p>

            {/* Show error details in development mode */}
            {!!isDev && (
              <div
                style={{
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px',
                  color: '#721c24',
                  marginBottom: '24px',
                  padding: '16px',
                  textAlign: 'left'
                }}
              >
                <strong style={{ display: 'block', marginBottom: '8px' }}>🐛 Debug Info (dev only):</strong>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Error:</strong> {error.name}
                </p>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Message:</strong> {error.message}
                </p>
                {!!error.digest && (
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Digest:</strong> {error.digest}
                  </p>
                )}
                {!!error.stack && (
                  <pre
                    style={{
                      backgroundColor: '#fff',
                      fontSize: '12px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      padding: '8px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {error.stack}
                  </pre>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={reset}
                style={{
                  backgroundColor: '#007bff',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '12px 24px'
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                style={{
                  backgroundColor: '#6c757d',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '12px 24px'
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
